import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { TbRefresh } from "react-icons/tb";
import { Settings2 } from "lucide-react";
import Pagination from "../../Component/Pagination";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  buildSimpleOptions,
  formatDateOnlyIST,
  getDuePillStyle,
} from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";
import "../training/trainingDashboard.css";

const WITHIN_DAYS_OPTIONS = [
  { value: 30, label: "Next 30 days" },
  { value: 60, label: "Next 60 days" },
  { value: 90, label: "Next 90 days" },
  { value: 180, label: "Next 180 days" },
  { value: 365, label: "Next 365 days" },
];

const INCLUDE_OVERDUE_OPTIONS = [
  { value: "true", label: "Include overdue" },
  { value: "false", label: "Due only (exclude overdue)" },
];

function MedicalDueOverduePage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [paginationKey, setPaginationKey] = useState(0);

  const [medicalTypes, setMedicalTypes] = useState([]);
  const [selectedMedicalType, setSelectedMedicalType] = useState(null);
  const [withinDays, setWithinDays] = useState(WITHIN_DAYS_OPTIONS[2]);
  const [includeOverdue, setIncludeOverdue] = useState(INCLUDE_OVERDUE_OPTIONS[0]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchLookups = async () => {
    try {
      const typesResponse = await hrRecordsApi.getMedicalTypes();
      setMedicalTypes(
        buildSimpleOptions(typesResponse || [], "type_name", "type_name"),
      );
    } catch (error) {
      console.error("Failed to load medical due/overdue filters:", error);
    }
  };

  const fetchRecords = async (
    currentPage = page,
    currentItemsPerPage = itemsPerPage,
  ) => {
    try {
      setLoading(true);
      const response = await hrRecordsApi.listMedicalDue({
        within_days: withinDays?.value || 90,
        include_overdue: includeOverdue?.value !== "false",
        medical_type: selectedMedicalType?.value || undefined,
        page: currentPage,
        per_page: currentItemsPerPage,
      });

      setRecords(response.items || []);
      setTotalItems(response.total_items || 0);
    } catch (error) {
      console.error("Failed to load due and overdue medical reports:", error);
      showSnackbar("Failed to load due and overdue medical reports", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchRecords(page, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, selectedMedicalType, withinDays, includeOverdue]);

  const resetToFirstPage = () => {
    setPage(1);
    setPaginationKey((key) => key + 1);
  };

  const handleRefresh = () => {
    setSelectedMedicalType(null);
    setWithinDays(WITHIN_DAYS_OPTIONS[2]);
    setIncludeOverdue(INCLUDE_OVERDUE_OPTIONS[0]);
    resetToFirstPage();
  };

  const daysUntilDue = (nextDueDate) => {
    if (!nextDueDate) return null;
    const due = new Date(nextDueDate);
    if (Number.isNaN(due.getTime())) return null;
    const today = new Date();
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.round((due.getTime() - today.getTime()) / 86400000);
  };

  const formatDaysUntilDue = (days) => {
    if (days === null || days === undefined) {
      return "NA";
    }

    return days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`;
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Medical Due &amp; Overdue</PageBackTitle>
              <div className="data-list-heading-content-right-side hr-records-toolbar">
                <div className="dropdown-container" style={{ minWidth: "190px" }}>
                  <Select
                    options={medicalTypes}
                    value={selectedMedicalType}
                    onChange={(option) => {
                      setSelectedMedicalType(option);
                      resetToFirstPage();
                    }}
                    placeholder="Filter by Medical Type"
                    isSearchable
                    isClearable
                  />
                </div>
                <div className="dropdown-container" style={{ minWidth: "160px" }}>
                  <Select
                    options={WITHIN_DAYS_OPTIONS}
                    value={withinDays}
                    onChange={(option) => {
                      setWithinDays(option || WITHIN_DAYS_OPTIONS[2]);
                      resetToFirstPage();
                    }}
                    placeholder="Within days"
                  />
                </div>
                <div className="dropdown-container" style={{ minWidth: "210px" }}>
                  <Select
                    options={INCLUDE_OVERDUE_OPTIONS}
                    value={includeOverdue}
                    onChange={(option) => {
                      setIncludeOverdue(option || INCLUDE_OVERDUE_OPTIONS[0]);
                      resetToFirstPage();
                    }}
                  />
                </div>
                <Tooltip title="Reminder Settings" arrow>
                  <button
                    type="button"
                    className="new-button hr-records-settings-button"
                    onClick={() => navigate("/hr-medical-reminder-settings")}
                  >
                    <Settings2 size={15} /> Settings
                  </button>
                </Tooltip>
                <Tooltip title="Refresh" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : records.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th>SR. NO.</th>
                        <th>EMPLOYEE</th>
                        <th>MEDICAL TYPE</th>
                        <th>EXAM DATE</th>
                        <th>VALID TO</th>
                        <th>NEXT DUE</th>
                        <th>DAYS</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {records.map((item, index) => {
                        const days = daysUntilDue(item.next_due_date);
                        return (
                        <tr
                          key={item.report_id}
                          className={`hoverable-row ${
                            days !== null && days < 0 ? "htd-row-overdue" : ""
                          }`}
                        >
                          <td>{(page - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            {item.person_name || "NA"}
                            {item.ec_no && (
                              <>
                                <br />
                                <span className="htd-muted-text">
                                  {item.ec_no}
                                </span>
                              </>
                            )}
                          </td>
                          <td>{item.medical_type || "NA"}</td>
                          <td>{formatDateOnlyIST(item.examination_date)}</td>
                          <td>{formatDateOnlyIST(item.valid_to)}</td>
                          <td>{formatDateOnlyIST(item.next_due_date)}</td>
                          <td>{formatDaysUntilDue(days)}</td>
                          <td className="center-alignment">
                            <span
                              className="hr-records-pill"
                              style={getDuePillStyle(item.status)}
                            >
                              {(item.status || "NA").replace(/_/g, " ")}
                            </span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No due or overdue medical reports found
                    </div>
                  </div>
                )}
              </div>

              <Pagination
                key={paginationKey}
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={(currentPage, perPage) => {
                  setPage(currentPage);
                  setItemsPerPage(perPage);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default MedicalDueOverduePage;
